import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputedCityInfoType } from '@/widgets';
import { LocationPointDto } from '@/app/store/api/restApi';
import { AddressSuggestion, useLazyGetGeocodeQuery, useLazyGetAddressSuggestionsQuery } from '@/entities';
import { useAppDispatch, setOrderPointError, setOrderInfoLoading } from '@/app/store';
import { getLocalizedReduxErrorMessage, getYandexLang, parseAddressFromGeocoder } from '@/shared/lib';

export const useGetOrderPoint = ({
  onSuccess,
  inputedCityInfo,
  setInputedCityInfo,
}: {
  onSuccess: VoidFunction;
  inputedCityInfo: InputedCityInfoType;
  setInputedCityInfo: ({ city, full, coordsCity, area }: InputedCityInfoType) => void;
}) => {
  const dispatch = useAppDispatch();
  const [triggerGeocode] = useLazyGetGeocodeQuery();
  const [triggerSuggest] = useLazyGetAddressSuggestionsQuery();
  const { t: tErrors } = useTranslation('errors');
  const { t, i18n } = useTranslation('validation');
  const [inputedCity, setInputedCity] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  const { city: opCity, full: opFullCity } = inputedCityInfo;

  const [debouncedValue] = useDebounce(inputedCity, 1000);

  useEffect(() => {
    const func = async () => {
      if (!debouncedValue.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await triggerSuggest({
          text: debouncedValue,
          lang: getYandexLang(i18n.language),
          types: 'locality',
        }).unwrap();

        setSuggestions(response);
      } catch (error) {
        toast.error(getLocalizedReduxErrorMessage(error ?? '', tErrors));
        dispatch(setOrderPointError(t('delivery.invalidAddress')));
      }
    };

    func();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, dispatch]);

  const handleSuggestionOrderPointSelect = async (suggestion: AddressSuggestion) => {
    setSuggestions([]);
    dispatch(setOrderInfoLoading(true));

    try {
      // Fetch coordinates using the URI
      const geocodeData = await triggerGeocode({
        uri: suggestion.uri,
        lang: getYandexLang(i18n.language),
      }).unwrap();

      if (!geocodeData.response || !geocodeData.response.GeoObjectCollection) {
        dispatch(setOrderPointError(t('delivery.invalidAddress')));
      } else {
        const geocodeObject = geocodeData.response.GeoObjectCollection.featureMember[0].GeoObject;

        const currentFullLocation = suggestion.value;
        setInputedCity(currentFullLocation);

        const { city } = parseAddressFromGeocoder(geocodeObject);

        const coords = geocodeObject.Point.pos.split(' ');
        const lat = parseFloat(coords[1]);
        const lng = parseFloat(coords[0]);
        const point: LocationPointDto = { lat, lng };

        const geoCodeArea = geocodeObject.boundedBy.Envelope;

        if (city !== opCity) {
          setInputedCityInfo({
            city,
            full: currentFullLocation,
            coordsCity: point,
            area: {
              lowerCorner: {
                lat: Number(geoCodeArea.lowerCorner.split(' ')[1]),
                lng: Number(geoCodeArea.lowerCorner.split(' ')[0]),
              },
              upperCorner: {
                lat: Number(geoCodeArea.upperCorner.split(' ')[1]),
                lng: Number(geoCodeArea.upperCorner.split(' ')[0]),
              },
            },
          });
        }
      }
    } catch (error) {
      toast.error(getLocalizedReduxErrorMessage(error ?? '', tErrors));
      dispatch(setOrderPointError(t('delivery.invalidAddress')));
    } finally {
      onSuccess();
      dispatch(setOrderInfoLoading(false));
    }
  };

  useEffect(() => {
    if (opFullCity) setInputedCity(opFullCity);
  }, [opFullCity]);

  return { inputedCity, setInputedCity, suggestions, handleSuggestionOrderPointSelect };
};
